import {
  cartApi,
  getTranslation,
  readKlaviyoLearnqProduct,
  trackKlaviyoAddedToCart,
  updateCart,
} from '../helpers';

const selectors = {
  footer: '[data-showcase-drawer-footer]',
  content: '[data-showcase-drawer-content]',
  subtotal: '[data-showcase-drawer-subtotal]',
  addAll: '[data-showcase-drawer-add-all]',
  addAllLabel: '[data-showcase-drawer-add-all-label]',
  productItem: '[data-product-item]',
  form: '[data-product-item-form]',
  variantSelect: '[data-product-item-variant-select]',
  sizeSelect: '[data-product-item-size-select]',
};

function formatMoney(cents, format) {
  if (typeof cents === 'string') {
    cents = cents.replace('.', '');
  }

  const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
  const formatString = format || '${{amount}}';

  function formatWithDelimiters(number, precision, thousands, decimal) {
    if (Number.isNaN(number) || number == null) {
      return '0';
    }

    const fixed = (number / 100).toFixed(precision ?? 2);
    const parts = fixed.split('.');
    const dollars = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${thousands ?? ','}`,
    );
    const centsPart = parts[1] ? `${decimal ?? '.'}${parts[1]}` : '';

    return dollars + centsPart;
  }

  const formatType = formatString.match(placeholderRegex)?.[1] ?? 'amount';
  let value = '';

  switch (formatType) {
    case 'amount':
      value = formatWithDelimiters(cents, 2);
      break;
    case 'amount_no_decimals':
      value = formatWithDelimiters(cents, 0);
      break;
    case 'amount_with_comma_separator':
      value = formatWithDelimiters(cents, 2, '.', ',');
      break;
    case 'amount_no_decimals_with_comma_separator':
      value = formatWithDelimiters(cents, 0, '.', ',');
      break;
    case 'amount_with_apostrophe_separator':
      value = formatWithDelimiters(cents, 2, "'", '.');
      break;
    default:
      value = formatWithDelimiters(cents, 2);
  }

  return formatString.replace(placeholderRegex, value);
}

function getAddAllButtonLabel(count) {
  const key =
    count === 1
      ? 'products.add_products_to_bag.one'
      : 'products.add_products_to_bag.other';

  return getTranslation(key, { count: String(count) });
}

export default class ShowcaseDrawerFooter {
  constructor(drawer, { onAddSuccess } = {}) {
    this.drawer = drawer;
    this.onAddSuccess = onAddSuccess;
    this.footer = drawer.querySelector(selectors.footer);
    if (!this.footer) {
      return;
    }

    this.content = drawer.querySelector(selectors.content);
    this.subtotalEl = this.footer.querySelector(selectors.subtotal);
    this.addAllBtn = this.footer.querySelector(selectors.addAll);
    this.labelEl = this.footer.querySelector(selectors.addAllLabel);
    this.moneyFormat = this.footer.dataset.moneyFormat;

    this._onAddAll = this._onAddAll.bind(this);
    this._onContentChange = this._onContentChange.bind(this);

    this.addAllBtn?.addEventListener('click', this._onAddAll);
    this.content?.addEventListener('change', this._onContentChange);
    this.content?.addEventListener(
      'product-item:updated',
      this._onContentChange,
    );

    this.update();
  }

  _onContentChange() {
    this.update();
  }

  _isFullySoldOut(itemEl, sizeSelect) {
    if (itemEl.dataset.productItemSoldOut === 'true') {
      return true;
    }

    if (!sizeSelect) {
      return false;
    }

    const sizeOptions = [...sizeSelect.options].filter(option => option.value);
    return (
      sizeOptions.length > 0 && sizeOptions.every(option => option.disabled)
    );
  }

  _getItemState(itemEl) {
    const form = itemEl.querySelector(selectors.form);
    const sizeSelect = itemEl.querySelector(selectors.sizeSelect);
    const hasSizeSelect = Boolean(sizeSelect);
    const basePrice =
      Number.parseInt(itemEl.dataset.productItemFromPrice, 10) || 0;
    const variantSelect = form?.querySelector(selectors.variantSelect);

    if (hasSizeSelect) {
      if (this._isFullySoldOut(itemEl, sizeSelect)) {
        return {
          hasSizeSelect: true,
          sizeSelected: false,
          soldOut: true,
          countsTowardTotal: false,
          addable: false,
          price: 0,
          itemEl,
          form,
        };
      }

      const sizeSelected = Boolean(sizeSelect?.value);

      if (!sizeSelected) {
        return {
          hasSizeSelect: true,
          sizeSelected: false,
          soldOut: false,
          countsTowardTotal: true,
          addable: false,
          price: basePrice,
          itemEl,
          form,
        };
      }

      const selectedOption = variantSelect?.selectedOptions[0];
      const soldOut = selectedOption?.hasAttribute('data-unavailable') ?? false;

      if (soldOut) {
        return {
          hasSizeSelect: true,
          sizeSelected: true,
          soldOut: true,
          countsTowardTotal: false,
          addable: false,
          price: 0,
          itemEl,
          form,
        };
      }

      return {
        hasSizeSelect: true,
        sizeSelected: true,
        soldOut: false,
        countsTowardTotal: true,
        addable: true,
        price: Number.parseInt(selectedOption?.dataset.price, 10) || basePrice,
        itemEl,
        form,
      };
    }

    const hiddenVariantInput = form?.querySelector('input[name="id"]');
    if (hiddenVariantInput?.value) {
      return {
        hasSizeSelect: false,
        sizeSelected: true,
        soldOut: false,
        countsTowardTotal: true,
        addable: true,
        price:
          Number.parseInt(hiddenVariantInput.dataset.variantPrice, 10) ||
          basePrice,
        itemEl,
        form,
      };
    }

    return {
      hasSizeSelect: false,
      sizeSelected: false,
      soldOut: false,
      countsTowardTotal: false,
      addable: false,
      price: 0,
      itemEl,
      form,
    };
  }

  _collectItems() {
    if (!this.content) {
      return [];
    }

    return [...this.content.querySelectorAll(selectors.productItem)].map(item =>
      this._getItemState(item),
    );
  }

  update() {
    if (!this.footer) {
      return;
    }

    const items = this._collectItems();
    const missingSizes = items.some(
      item => item.hasSizeSelect && !item.sizeSelected && !item.soldOut,
    );
    const countableItems = items.filter(item => item.countsTowardTotal);
    const addableItems = items.filter(item => item.addable);
    const totalCents = countableItems.reduce(
      (sum, item) => sum + item.price,
      0,
    );
    const countableCount = countableItems.length;

    if (this.subtotalEl) {
      this.subtotalEl.textContent = formatMoney(totalCents, this.moneyFormat);
    }

    if (this.labelEl) {
      if (missingSizes) {
        this.labelEl.textContent = getTranslation('products.select_sizes');
      } else {
        this.labelEl.textContent = getAddAllButtonLabel(countableCount);
      }
    }

    if (this.addAllBtn) {
      if (missingSizes) {
        this.addAllBtn.disabled = true;
        return;
      }

      this.addAllBtn.disabled = addableItems.length === 0;
    }
  }

  _onAddAll() {
    if (!this.addAllBtn || this.addAllBtn.disabled) {
      return;
    }

    const items = this._collectItems();
    const missingSizes = items.some(
      item => item.hasSizeSelect && !item.sizeSelected && !item.soldOut,
    );

    if (missingSizes) {
      this.update();
      return;
    }

    const addableItems = items.filter(item => item.addable);
    if (!addableItems.length) {
      this.update();
      return;
    }

    const cartItems = addableItems.map(({ itemEl, form }) => {
      const formData = new FormData(form);
      return cartApi.formatFormData(formData, {
        finalSale: itemEl.dataset.finalSale === 'true',
        preorder: itemEl.dataset.preorder !== undefined,
        preorderText: itemEl.dataset.preorder,
      });
    });

    const originalLabel = this.labelEl?.textContent || '';
    this.addAllBtn.disabled = true;
    if (this.labelEl) {
      this.labelEl.textContent = getTranslation('products.adding');
    }

    cartApi
      .add({ items: cartItems })
      .then(response => {
        if (response.error) {
          throw response;
        }

        addableItems.forEach(({ itemEl }) => {
          trackKlaviyoAddedToCart(readKlaviyoLearnqProduct(itemEl));
        });

        if (response.sections) {
          updateCart({ sections: response.sections, openCart: true });
        }

        this.onAddSuccess?.();
      })
      .catch(() => {
        if (this.labelEl) {
          this.labelEl.textContent = originalLabel;
        }
        this.update();
      });
  }
}
